import Preloader from '~/components/Shared/Preloader';
import { getOrder } from '~/helpers/backendHelper';
import { route } from '~/helpers/routeHelper';
import { isUserVerifiedLocal } from '~/helpers/tokenHelper';
import useFirstRender from '~/hooks/firstRender';
import Order from '~/model/order';
import React, { useEffect, useState } from 'react';
import { useAuth } from './auth';
import { TRoute } from '~/types/model';
import OrderSigner from '~/model/orderSigner';
import { useAppointment } from './appointment';
import OrderNotary from '~/model/orderNotary';

type NavigatorContextValue = {
  getNextRoute: () => string;
  navIsReady: () => boolean;
  setRoutes: (routes: TRoute[]) => void;
};

const NavigatorContext = React.createContext<NavigatorContextValue>({} as NavigatorContextValue);

const Navigator = (props: any) => {

  const { user: authUser } = useAuth();

  const { isFirstRender } = useFirstRender();

  const { order: orderAppointment, isBusy: isBusyAppointment } = useAppointment();

  const ROUTE_HOME = route('home') as string;
  const ROUTE_MESSAGES = route('messages') as string;
  const ROUTE_VID = route('vid') as string;
  const ROUTE_INSURANCE = route('insurance') as string;
  const ROUTE_ADDITIONAL_INFORMATION = route('additional') as string;
  const ROUTE_APPOINTMENT = route('appointment') as string;
  const ROUTE_FINANCIAL = route('finance') as string;
  const ROUTE_SIGNATURES = route('signatures') as string;
  const ROUTE_FINISH = route('finish') as string;

  // ordered list of routes the user can navigate to
  const [routes, setRoutes] = useState<TRoute[]>([]);
  // the order fetched from the backend
  const [order, setOrder] = useState<Order | null>(null);
  // state for loading order
  const [isLoadingOrder, setIsLoadingOrder] = useState(false);

  // we are able to navigate when routes are loaded from api
  const navIsReady = () => !!routes.length;

  // object that keeps track of routes order
  const routesOrder = {
    [ROUTE_HOME]: 0,
    [ROUTE_MESSAGES]: 0, // messages and home hold the same value because they both route to Home (the chat is render in a modal)
    [ROUTE_VID]: 1,
    [ROUTE_INSURANCE]: 2,
    [ROUTE_ADDITIONAL_INFORMATION]: 3,
    [ROUTE_FINANCIAL]: 4,
    [ROUTE_SIGNATURES]: 5,
    [ROUTE_APPOINTMENT]: 5, // signatures and appointment hold the same value because they can be done in parallel
    [ROUTE_FINISH]: 6,
  }

  const inStoreRoutesOrder = {
    [ROUTE_HOME]: 0,
    [ROUTE_INSURANCE]: 1,
    [ROUTE_FINISH]: 2,
  }

  // runs whenever the `authUser` changes, including at component mount
  useEffect(() => {
    if (authUser) {
      // don't fetch order until we are allowed to make backend calls
      fetchOrder();
    }
  }, [authUser]);

  useEffect(() => {
    redirectToCorrectRoute();
  }, [window.location.pathname, navIsReady()])

  // If a user skips a step by going to a different route, we need to redirect them to the correct route
  // the steps must be completed in order, so if they skip a step, we need to redirect them to the correct route
  const redirectToCorrectRoute = () => {
    if (!order || !navIsReady()) return;
    const currentRoute = window.location.pathname;

    // VID is the first route, so if we are on it, we don't need to redirect
    // only if there is no VID service selected
    if (currentRoute === route('vid')!) {
      if (!order.isVidRequired) {
        window.location.pathname = decideCorrectRoute();
        return;
      }
    }

    // Insurance must be as a service and not already completed
    if (currentRoute === route('insurance')!) {
      if (!order.isInsuranceVerificationRequired) {
        window.location.pathname = decideCorrectRoute();
        return;
      }
      if (order.status < Order.STATUS_PENDING_INSURANCE_VERIFICATION) {
        window.location.pathname = decideCorrectRoute();
        return;
      }
    }

    // Guarantee must be as a service and not already completed
    if (currentRoute === route('additional')!) {
      if (!order.isGuaranteeRequired) {
        window.location.pathname = decideCorrectRoute();
        return;
      }
      if (order.status < Order.STATUS_PENDING_SECOND_FORM_OF_ID) {
        window.location.pathname = decideCorrectRoute();
        return;
      }
    }

    // Signatures must have at least inksign or esign selected as services
    if (currentRoute === route('signatures')!) {
      if (!order.isEsignRequired && !order.isInkSignRequired) {
        window.location.pathname = decideCorrectRoute();
        return;
      }

      if (order.status < Order.STATUS_NEW) {
        window.location.pathname = decideCorrectRoute();
        return;
      }
    }

    // Appointment , notary must be as a service and not already completed
    if (currentRoute === route('appointment')!) {
      if (!order.isNotaryRequired || (!orderAppointment && !isBusyAppointment)) {
        window.location.pathname = decideCorrectRoute();
        return;
      }
      if (order.status < Order.STATUS_NEW) {
        window.location.pathname = decideCorrectRoute();
        return;
      }
    }

    // Financial must be a service and not already completed
    if (currentRoute === route('finance')!) {
      if (order.docDeliveryOption !== Order.DOC_DELIVERY_OPTION_UPLOAD) {
        window.location.pathname = decideCorrectRoute();
        return;
      }
      if (order.status < Order.STATUS_NEW) {
        window.location.pathname = decideCorrectRoute();
        return;
      }
    }
    // Ink sign must be a service and not already completed
    if (currentRoute === route('ink_signature')!) {
      if (!order.isInkSignRequired) {
        window.location.pathname = decideCorrectRoute();
        return;
      }
      if (order.status < Order.STATUS_NEW) {
        window.location.pathname = decideCorrectRoute();
        return;
      }
    }

    // E-sign must be a service and not already completed
    if (currentRoute === route('e_signature')!) {
      if (!order.isEsignRequired) {
        window.location.pathname = decideCorrectRoute();
        return;
      }
      if (order.status < Order.STATUS_NEW) {
        window.location.pathname = decideCorrectRoute();
        return;
      }
    }
  }

  const decideCorrectRoute = () => {
    // if order is in Pending Vid we need to redirect to VID
    if (order?.status === Order.STATUS_PENDING_VID) {
      return route('vid')!;
    }
    // if order is in Pending Insurance we need to redirect to Insurance
    if (order?.status === Order.STATUS_PENDING_INSURANCE_VERIFICATION) {
      return route('insurance')!;
    }
    // if order is in Pending Second Form of Id we need to redirect to Additional Information
    if (order?.status === Order.STATUS_PENDING_SECOND_FORM_OF_ID) {
      return route('additional')!;
    }
    // if order notary is in appointment set we need to redirect to Appointment
    if (orderAppointment?.orderNotary?.status === OrderNotary.STATUS_APPOINTMENT_SET && !!order?.isNotaryRequired) {
      return route('appointment')!;
    }
    // if order is Pending Ink Sign or Esign we need to redirect to Signatures
    if (order?.status! >= Order.STATUS_NEW) {
      // if delivery option is upload, we need to redirect to Finance
      if (order?.docDeliveryOption === Order.DOC_DELIVERY_OPTION_UPLOAD) {
        return route('finance')!;
      }
      // if delivery option is not upload, we need to redirect to Signatures
      return route('signatures')!;
    }
    // if no conditions are met, return home
    return route('home')!;
  }

  const fetchOrder = () => {
    setIsLoadingOrder(true);
    getOrder().then(res => {
      const order = res.order;
      if (!order) {
        return;
      }
      setOrder(order);
      if (order.customerLocation === Order.CUSTOMER_LOCATION_IN_STORE) {
        // determine accessible routes for this order
        setRoutes([
          {
            route: ROUTE_HOME,
            status: true,
          },
          {
            route: ROUTE_INSURANCE,
            status: order.isInsuranceVerificationRequired && authUser?.position === OrderSigner.POSITION_MAIN_SIGNER,
          },
          {
            route: ROUTE_FINISH,
            status: true,
          }
        ])
      } else {
        // determine accessible routes for this order
        setRoutes([
          {
            route: ROUTE_HOME,
            status: true,
          },
          {
            route: ROUTE_VID,
            status: order.isVidRequired,
          },
          {
            route: ROUTE_INSURANCE,
            status: order.isInsuranceVerificationRequired && authUser?.position === OrderSigner.POSITION_MAIN_SIGNER,
          },
          {
            route: ROUTE_ADDITIONAL_INFORMATION,
            status: (authUser?.position === OrderSigner.POSITION_MAIN_SIGNER && (!!order.isInsuranceVerificationRequired ? !!order.isGuaranteeRequired && !authUser?.hasInsurance : !!order.isGuaranteeRequired)),
          },
          {
            route: ROUTE_FINANCIAL,
            status: order.docDeliveryOption == Order.DOC_DELIVERY_OPTION_UPLOAD,
          },
          {
            route: ROUTE_SIGNATURES,
            status: order.isEsignRequired || order.isInkSignRequired,
          },
          {
            route: ROUTE_APPOINTMENT,
            status: order.isNotaryRequired, // appointment feature is only available for order with notary
          },
          {
            route: ROUTE_FINISH,
            status: true,
          }
        ])
      }
    })
      .finally(() => {
        setIsLoadingOrder(false);
      });
  }

  // helper method that determines the next available route
  const getNextRoute = () => {
    const currentRoute = window.location.pathname;
    return findNextAvailableRoute(currentRoute);
  }
  // iterates the route array and finds the next route with status = true,
  // and with index greater than current route's index
  const findNextAvailableRoute = (currentRoute: string) => {
    let nextAvailableRoute = '';
    routes.some((elem, index) => {
      if (order?.customerLocation === Order.CUSTOMER_LOCATION_IN_STORE) {
        if (index > inStoreRoutesOrder[currentRoute as keyof typeof inStoreRoutesOrder] && elem.status) {
          nextAvailableRoute = elem.route as string;
          // persist query params from /messages
          if (currentRoute == route('messages') && window.location.search) {
            nextAvailableRoute += window.location.search;
          }
          // break loop as soon as we find the first available route
          return true;
        } else {
          return false;
        }
      } else {
        if (index > routesOrder[currentRoute as keyof typeof routesOrder] && elem.status) {
          nextAvailableRoute = elem.route as string;
          // persist query params from /messages
          if (currentRoute == route('messages') && window.location.search) {
            nextAvailableRoute += window.location.search;
          }
          // break loop as soon as we find the first available route
          return true;
        } else {
          return false;
        }
      }
    })
    return nextAvailableRoute;
  }

  // when refreshing and still verified, show preloader until routes are loaded
  if (isFirstRender && !navIsReady() && isUserVerifiedLocal()) {
    return <Preloader />
  }

  return <NavigatorContext.Provider value={{ getNextRoute, navIsReady, setRoutes, order, isLoadingOrder }} {...props} />
}

// helper hook that makes context data available
export const useNavigator = () => React.useContext(NavigatorContext);

export default Navigator;